import { trpc } from '../../api/trpc';

const STANDARD_SUGGESTIONS = [
  'Triage alerts',
  'Investigate host',
  'Latest threat intelligence',
  'Security posture',
  'Review access',
];

const POSTURE_PROMPT = 'Run a posture assessment on my Entra tenant';

interface HomeAllCaughtUpProps {
  onSuggestion: (text: string) => void;
}

/**
 * Empty-state chip rail. Day-one Entra posture suggestion (spec §7.2 Surface A):
 * when the Microsoft Entra connector is connected AND no prior PostureAssessment
 * exists for the tenant, prepend "Run an Entra posture check" as the first chip.
 * After the first run, the chip text changes to "Re-run posture check" and
 * loses its prominent first-position slot.
 */
export function HomeAllCaughtUp({ onSuggestion }: HomeAllCaughtUpProps) {
  const { data: connectors } = trpc.connectors.list.useQuery();
  const { data: postureStatus } = trpc.home.postureStatus.useQuery();

  // The Microsoft connector covers Entra. In this codebase productType is
  // 'microsoft' (not 'entra') and the activated status is 'active' (not
  // 'connected'). See services/connector/src/service.ts.
  const entraConnected = (connectors ?? []).some(
    (c: { productType: string; status: string }) =>
      c.productType === 'microsoft' && c.status === 'active',
  );
  const hasPriorAssessment = postureStatus?.hasPriorAssessment ?? false;

  // Build the chip list per the spec's trigger rules.
  let chips: Array<{ label: string; prompt: string }>;
  if (entraConnected && !hasPriorAssessment) {
    // First-run state: lead with the posture suggestion.
    chips = [
      { label: 'Run an Entra posture check', prompt: POSTURE_PROMPT },
      ...STANDARD_SUGGESTIONS.map((s) => ({ label: s, prompt: s })),
    ];
  } else if (entraConnected && hasPriorAssessment) {
    // Re-run state: chip joins the standard set in normal order, with re-run wording.
    chips = [
      ...STANDARD_SUGGESTIONS.map((s) => ({ label: s, prompt: s })),
      { label: 'Re-run posture check', prompt: POSTURE_PROMPT },
    ];
  } else {
    // No Entra connector: no posture chip at all.
    chips = STANDARD_SUGGESTIONS.map((s) => ({ label: s, prompt: s }));
  }

  return (
    <div className="home-caught-up">
      <p className="home-caught-up-subtitle">You're all caught up, anything you want to chat about?</p>
      <div className="suggestion-chips">
        {chips.map((c) => (
          <button
            key={c.label}
            className="suggestion-chip"
            onClick={() => onSuggestion(c.prompt)}
            type="button"
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}
